import type { LearnerShellProps } from '~/elc/features/learner';
import type { SortOptions } from '../../types';
import type { SortDirection } from '~/types';
import { useState } from 'react';
import { I18n } from '~/common/explicit-exports';
import { LearnerShell, PageWrapper } from '~/elc/features/learner';
import { useLoader } from '~/hooks';
import { SavedContentView } from '../../components';
import { useSavedContentQuery } from '../../api';
import { SAVED_CONTENT_PER_PAGE } from '../../constants';

type SortSelection = { primaryKey: SortOptions; secondaryKey: SortDirection };

const RECENTLY_ADDED = I18n.t('js.learner.saved_content.sort.recently_added');
const TITLE = I18n.t('js.learner.saved_content.sort.title');
const NEWEST_TO_OLDEST = I18n.t('js.learner.saved_content.sort.newest_to_oldest');
const OLDEST_TO_NEWEST = I18n.t('js.learner.saved_content.sort.oldest_to_newest');
const Z_TO_A = I18n.t('js.learner.saved_content.sort.z_to_a');
const A_TO_Z = I18n.t('js.learner.saved_content.sort.a_to_z');

const sortOptions = [
  {
    key: 'createdAt',
    name: RECENTLY_ADDED,
    options: [
      { key: 'desc', name: NEWEST_TO_OLDEST },
      { key: 'asc', name: OLDEST_TO_NEWEST },
    ],
  },
  {
    key: 'title',
    name: TITLE,
    options: [
      { key: 'asc', name: A_TO_Z },
      { key: 'desc', name: Z_TO_A },
    ],
  },
];

export function LearnerSavedContent({ authToken }: LearnerShellProps) {
  const [page, setPage] = useState(1);
  const [sortSelection, setSortSelection] = useState<SortSelection>({
    primaryKey: 'createdAt',
    secondaryKey: 'desc',
  });

  const savedContentQuery = useSavedContentQuery({
    contentIds: [],
    page,
    items: SAVED_CONTENT_PER_PAGE,
    sortCol: sortSelection.primaryKey,
    sortDirection: sortSelection.secondaryKey,
  });

  useLoader(savedContentQuery.isLoading);

  return (
    <LearnerShell authToken={authToken}>
      <PageWrapper>
        {!savedContentQuery.isLoading && (
          <SavedContentView
            savedContent={savedContentQuery.data?.results ?? []}
            sortOptions={sortOptions}
            sortSelection={sortSelection}
            onSortChange={(selection) => setSortSelection(selection as SortSelection)}
            onPageChange={setPage}
            page={page}
            totalItems={savedContentQuery.data?.count ?? 0}
          />
        )}
      </PageWrapper>
    </LearnerShell>
  );
}
